/**
 * Calidad de entregas — rediseño from scratch.
 *
 * Producto SaaS de ops de last-mile: el manager quiere saber en 5s si hay
 * algo en llamas y, si no, dónde mejorar. Diseño editorial, narrativo, una
 * sola columna scrolleable (sin tabs como tenía la versión anterior con 7).
 *
 * Bans aplicados (impeccable laws):
 *  · NO hero-metric template (big-number + label + gradient).
 *  · NO side-stripe borders (acabamos de sacarlos del tema personalizado).
 *  · NO identical card grids (criterios van en un único bloque).
 *  · NO modal-first (drivers expanden inline o navegan al perfil).
 *  · NO em dashes en copy.
 *  · NO gradient text.
 *
 * Orden de secciones (por urgencia del ops manager):
 *  1) Alertas activas (lo que necesita acción ahora)
 *  2) Headline narrativo + sparkline + funnel inline
 *  3) Criterios por impacto (ordenados worst-first)
 *  4) Drivers que necesitan atención (worst N)
 *  5) Tendencia (¿mejoro?)
 *  6) Top performers (al pie, deprioritizado)
 *
 * Comunas: chip de filtro contextual en el header (no sección propia) cuando
 * se enchufe la API by-municipality como faceta.
 */
import { useEffect, useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertTriangle, ArrowDownRight, ArrowUpRight, Camera, Clock, FileText,
  Image as ImageIcon, MapPin, Minus, ShieldCheck, TrendingUp, User, UserCheck,
} from 'lucide-react';
import dayjs from 'dayjs';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  AreaChart, Area,
} from 'recharts';
import useAuthScope from '../../../hooks/useAuthScope';
import { useTheme } from '../../../hooks/useTheme';
import { useUrlState, useUrlStateBatch } from '../../../hooks/useUrlState';
import DateRangeFilter, { rangeFromPreset, DEFAULT_PRESETS } from '../../../components/dateRangeFilter/DateRangeFilter';
import { Skeleton } from '../../../components/ui/Skeleton';
import { EmptyState } from '../../../components/ui/EmptyState';
import { Badge } from '../../../components/ui/Badge';
import deliveryQualityService from '../../../services/deliveryQualityService';
import DriverAuditPanel from './components/DriverAuditPanel';
import { formatNumberCL, formatPercent } from '../../../utils/formatters';
import { cn } from '../../../utils/cn';
import './DeliveryQualityNext.scss';

const CRITERION_META = {
  // Orden de menor pass_rate típico arriba — pero se reordena en runtime.
  image_count: { label: 'Cantidad de fotos',     icon: Camera,      hint: 'Mínimo 2 fotos por entrega' },
  images_ai:   { label: 'Análisis IA de fotos',  icon: ImageIcon,   hint: 'Foto validada por IA externa' },
  timeliness:  { label: 'Puntualidad',           icon: Clock,       hint: 'Entrega antes de 21:00' },
  distance:    { label: 'Distancia al punto',    icon: MapPin,      hint: 'Capturada a menos de 100 m' },
  receiver:    { label: 'Nombre del receptor',   icon: UserCheck,   hint: 'Receptor identificado' },
  rut:         { label: 'RUT del receptor',      icon: FileText,    hint: 'Formato válido' },
};

const PRESETS = [
  { key: 'today', quick: true },
  { key: 'yesterday', quick: true },
  { key: '7d', quick: true },
  { key: '30d', quick: true },
  { key: 'thisMonthToToday', quick: false },
  { key: 'thisMonth', quick: false },
  { key: 'lastMonth', quick: false },
  { key: '90d', quick: false },
];

export default function DeliveryQualityNext() {
  const { t } = useTranslation('deliveryquality');
  const { isDark } = useTheme();
  const { roleType, companyId, logisticCompanyId } = useAuthScope();

  // Preset persistido en la URL (?preset=). Default '7d' ⇒ sin param se comporta
  // IDÉNTICO a antes (param omitido cuando vale el default). Habilita deep-link.
  const [preset] = useUrlState('preset', '7d');
  // El rango 'custom' no es reconstruible desde el preset solo: lo persistimos
  // aparte en ?from/?to para poder rehidratarlo en reload/back.
  const [fromUrl] = useUrlState('from', '');
  const [toUrl] = useUrlState('to', '');
  // Setter atómico para cambiar preset + from/to en UNA sola navegación (en
  // react-router v6 dos setSearchParams encadenados en el mismo handler se pisan).
  const setDateParams = useUrlStateBatch({
    preset: { defaultValue: '7d' },
    from: { defaultValue: '' },
    to: { defaultValue: '' },
  });
  // Siembra SÍNCRONA del rango desde la URL en el primer render: si el preset es
  // 'custom' y hay ?from/?to los reconstruimos; si no, derivamos la ventana del
  // preset. Incluido el default '7d', ahora simétrico con el deep-link de
  // cualquier otro preset (antes el default dejaba el range vacío y fetcheaba el
  // histórico completo en vez de los últimos 7 días).
  const [range, setRange] = useState(() => {
    if (preset === 'custom' && fromUrl && toUrl) return { start: fromUrl, end: toUrl };
    const [s, e] = rangeFromPreset(preset === 'custom' ? '7d' : preset);
    return { start: s.format('YYYY-MM-DD'), end: e.format('YYYY-MM-DD') };
  });

  // Re-deriva el rango cuando cambia el preset (selección / back-forward) o, para
  // 'custom', cuando cambian ?from/?to. El guard de contenido evita re-render y
  // re-fetch redundantes cuando el valor derivado no cambió.
  useEffect(() => {
    let next;
    if (preset === 'custom') {
      if (!fromUrl || !toUrl) return;
      next = { start: fromUrl, end: toUrl };
    } else {
      const [s, e] = rangeFromPreset(preset);
      next = { start: s.format('YYYY-MM-DD'), end: e.format('YYYY-MM-DD') };
    }
    setRange((prev) => (prev.start === next.start && prev.end === next.end ? prev : next));
  }, [preset, fromUrl, toUrl]);

  const handleRangeChange = useCallback((start, end, p) => {
    setRange({ start, end });
    if (p === 'custom') {
      // Custom: persistimos preset + fechas concretas para reconstruir en reload/back.
      setDateParams({ preset: 'custom', from: start, to: end });
    } else if (p) {
      // Volver a un preset limpia ?from/?to.
      setDateParams({ preset: p, from: '', to: '' });
    }
  }, [setDateParams]);

  const scope = useMemo(() => {
    if (roleType === 'logistic' && logisticCompanyId) return { logistic_company_id: logisticCompanyId };
    if (roleType === 'company' && companyId) return { company_id: companyId };
    return {};
  }, [roleType, companyId, logisticCompanyId]);

  const filters = useMemo(() => ({
    date_from: range.start ? `${range.start}T00:00:00` : undefined,
    date_to: range.end ? `${range.end}T23:59:59` : undefined,
    ...scope,
  }), [range, scope]);

  // Rango previo del mismo tamaño para calcular delta.
  const prevFilters = useMemo(() => {
    if (!range.start || !range.end) return null;
    const days = dayjs(range.end).diff(dayjs(range.start), 'day') + 1;
    const prevEnd = dayjs(range.start).subtract(1, 'day').format('YYYY-MM-DD');
    const prevStart = dayjs(prevEnd).subtract(days - 1, 'day').format('YYYY-MM-DD');
    return {
      date_from: `${prevStart}T00:00:00`,
      date_to: `${prevEnd}T23:59:59`,
      ...scope,
    };
  }, [range, scope]);

  // El endpoint /global es el "source of truth" del período: si no hay
  // evaluaciones, ninguna sección secundaria tiene sentido (renderizarlas en
  // skeleton mientras coverage/by-driver/timeline también dan vacío sería
  // ruido visual). Lift up: el page hace el fetch una vez y reparte data.
  const global = useEndpoint(() => deliveryQualityService.getGlobal(filters), [filters]);
  const prevGlobal = useEndpoint(
    () => (prevFilters ? deliveryQualityService.getGlobal(prevFilters) : Promise.resolve({ data: null })),
    [prevFilters],
  );
  const hasData = !global.loading && global.data && global.data.total_evaluations > 0;

  // Cobertura del período (entregadas vs evaluadas), levantada a la página: la
  // consumen Overview (ratio / score parcial) y FunnelStrip (embudo). Antes cada
  // una la fetcheaba por su lado — misma request, doble red.
  const coverage = useEndpoint(() => deliveryQualityService.getCoverage(filters), [filters]);

  // Repartidor seleccionado para auditar, persistido en la URL (?driver=<key>):
  // deep-linkable y el botón Atrás cierra el panel. Default null ⇒ param omitido.
  const [selectedDriver, setSelectedDriver] = useUrlState('driver', null);
  // Levantado a la página: antes DriversAttention y TopPerformers fetcheaban
  // by-driver por separado (mismo request, doble red). Una sola lectura repartida.
  const drivers = useEndpoint(() => deliveryQualityService.getByDriver({ ...filters, limit: 100 }), [filters]);
  const driverList = useMemo(() => {
    const d = drivers.data;
    if (!d) return [];
    return d.drivers || d.items || d.data || (Array.isArray(d) ? d : []);
  }, [drivers.data]);
  // Fila del repartidor abierto (para cabecera del panel con nombre/score sin
  // re-fetch). En deep-link puede no estar en la lista: el panel cae a breakdown.
  const selectedRow = useMemo(
    () => driverList.find((d) => (d.key || d.driver_id || d.id) === selectedDriver) || null,
    [driverList, selectedDriver],
  );

  return (
    <div className={cn('dq-page', selectedDriver && 'dq-page--auditing')}>
      <Header preset={preset} customRange={preset === 'custom' ? range : null} onRangeChange={handleRangeChange} />
      <Overview state={global} prevState={prevGlobal} coverage={coverage} />
      {hasData && (
        <>
          <FunnelStrip state={coverage} />
          <CriteriaImpact data={global.data} />
          <DriversAttention state={drivers} onSelect={setSelectedDriver} selectedId={selectedDriver} />
          <TrendChart filters={filters} isDark={isDark} />
          <TopPerformers state={drivers} onSelect={setSelectedDriver} selectedId={selectedDriver} />
        </>
      )}
      {selectedDriver && (
        <DriverAuditPanel
          driverId={selectedDriver}
          driverRow={selectedRow}
          filters={filters}
          isDark={isDark}
          onClose={() => setSelectedDriver(null)}
        />
      )}
    </div>
  );
}

// ─── Header ──────────────────────────────────────────────────────────────────

function Header({ preset, customRange, onRangeChange }) {
  const { t } = useTranslation('deliveryquality');
  return (
    <header className="dq-header">
      <div className="dq-header__title">
        <h1>{t('Calidad de entregas')}</h1>
        <p>{t('Qué tan bien están entregando tus repartidores.')}</p>
      </div>
      <DateRangeFilter value={preset} customRange={customRange} onChange={onRangeChange} presets={PRESETS} />
    </header>
  );
}

// ─── Overview headline ───────────────────────────────────────────────────────
// Frase narrativa con números embebidos. Reemplaza el clásico hero-metric.

function Overview({ state: cur, prevState: prev, coverage }) {
  const { t } = useTranslation('deliveryquality');
  // Cobertura del período (entregadas vs evaluadas), levantada en la página y
  // pasada como prop: su única función es decir si el promedio es representativo
  // (un score alto sobre el 20% de las entregas no es lo mismo que sobre el 95%).

  if (cur.loading) return <Skeleton className="h-28 rounded-xl mb-8" />;
  if (cur.error || !cur.data || !cur.data.total_evaluations) {
    return (
      <section className="dq-overview dq-overview--empty">
        <EmptyState
          icon={<ShieldCheck />}
          title={t('Sin evaluaciones en este rango')}
          description={t('Cuando se evalúen entregas aparecerá el score acá.')}
        />
      </section>
    );
  }

  const total = cur.data.total_evaluations;
  const score = Math.round((cur.data.avg_score || 0) * 10) / 10;
  const passRate = passRateFromCriteria(cur.data.by_criterion);
  const pass100 = Math.round(passRate * 100);

  const prevScore = prev?.data?.avg_score ?? null;
  const deltaScore = prevScore != null ? Math.round((cur.data.avg_score - prevScore) * 10) / 10 : null;

  // Cobertura = entregas únicas evaluadas / entregadas en el mismo rango.
  // `pending_evaluation` es el gap que reporta el back; si no viene, lo
  // derivamos. Sólo calculamos el ratio cuando hubo entregas que medir.
  const cov = coverage.data;
  const delivered = cov?.delivered ?? 0;
  const evaluatedUnique = cov?.evaluated ?? null;
  const covRatio = delivered > 0 && evaluatedUnique != null ? evaluatedUnique / delivered : null;
  const covGap = cov?.pending_evaluation ?? (evaluatedUnique != null ? Math.max(delivered - evaluatedUnique, 0) : null);
  // Umbral de confianza: bajo 85% el promedio cubre una fracción y conviene
  // avisar; bajo 60% es directamente poco representativo.
  const covLow = covRatio != null && covRatio < 0.85;

  return (
    <section className="dq-overview">
      <p className="dq-overview__lede">
        {t('Tu equipo evaluó')}{' '}
        <strong>{formatNumberCL(total)} {t('entregas')}</strong>.{' '}
        <strong>{pass100} {t('de cada 100')}</strong>{' '}
        {t('pasaron todos los criterios.')}
      </p>
      <div className="dq-overview__meta">
        <Metric label={t('Score promedio')} value={score} fmt="score" />
        {covRatio != null && (
          <Metric
            label={t('Cobertura de evaluación')}
            value={covRatio}
            fmt="pct"
            tone={covLow ? 'warning' : undefined}
          />
        )}
        <Metric label={t('Tarde (>21:00)')} value={cur.data.late_count} fmt="int" tone="warning" />
        <Metric label={t('Sin fotos mínimas')} value={cur.data.missing_images_count} fmt="int" tone="warning" />
        {deltaScore != null && (
          <Delta value={deltaScore} label={t('vs período anterior')} />
        )}
      </div>
      {covLow && covGap > 0 && (
        <p className="dq-overview__coverage">
          <Badge tone={covRatio < 0.6 ? 'error' : 'warning'} dot>
            {t('Score parcial')}
          </Badge>
          <span>
            {t('Se evaluó el')}{' '}
            <strong>{formatPercent(covRatio, 0)}</strong>{' '}
            {t('de lo entregado')} ({formatNumberCL(covGap)} {t('entregas sin evaluar')}).{' '}
            {t('El promedio refleja sólo esa muestra.')}
          </span>
        </p>
      )}
    </section>
  );
}

function Metric({ label, value, fmt = 'int', tone }) {
  let formatted;
  if (fmt === 'score') formatted = (Math.round(value * 10) / 10).toFixed(1);
  else if (fmt === 'pct') formatted = formatPercent(value, 0);
  else formatted = formatNumberCL(value ?? 0);
  return (
    <div className={cn('dq-metric', tone && `dq-metric--${tone}`)}>
      <span className="dq-metric__value">{formatted}</span>
      <span className="dq-metric__label">{label}</span>
    </div>
  );
}

function Delta({ value, label }) {
  const positive = value > 0;
  const negative = value < 0;
  const Icon = positive ? ArrowUpRight : negative ? ArrowDownRight : Minus;
  const cls = positive ? 'dq-delta--up' : negative ? 'dq-delta--down' : 'dq-delta--flat';
  const sign = positive ? '+' : '';
  return (
    <div className={cn('dq-delta', cls)}>
      <Icon className="dq-delta__icon" />
      <span className="dq-delta__value">{sign}{value.toFixed(1)}</span>
      <span className="dq-delta__label">{label}</span>
    </div>
  );
}

// ─── Funnel ──────────────────────────────────────────────────────────────────
// Horizontal step-bar de Entregadas → Evaluadas → IA → OK/Dudosa/Rechazada.
// Cada step muestra count + % vs total entregado.

function FunnelStrip({ state }) {
  const { t } = useTranslation('deliveryquality');
  const { data, loading, error } = state;

  if (loading) return <Skeleton className="h-20 rounded-xl mb-8" />;
  if (error || !data) return null;

  // Shape real de getCoverage (igual que QualityFunnel): delivered / evaluated /
  // with_ai / ai_validated / pending_evaluation. El endpoint NO desglosa "dudosa"
  // ni "rechazada", así que el embudo muestra las 4 etapas que sí existen. Antes
  // leía *_count inexistentes → todo 0 → la sección quedaba siempre en blanco.
  const delivered = data.delivered ?? 0;
  const evaluated = data.evaluated ?? 0;
  const withAi = data.with_ai ?? 0;
  const validated = data.ai_validated ?? 0;

  const steps = [
    { key: 'delivered', label: t('Entregadas'),  count: delivered, tone: 'neutral' },
    { key: 'evaluated', label: t('Evaluadas'),   count: evaluated, tone: 'info' },
    { key: 'ai',        label: t('Con IA'),      count: withAi,    tone: 'info' },
    { key: 'validated', label: t('IA validada'), count: validated, tone: 'success' },
  ];

  if (delivered === 0) return null;

  return (
    <section className="dq-funnel" aria-label={t('Embudo de calidad')}>
      {steps.map((s) => {
        const pct = delivered > 0 ? (s.count / delivered) * 100 : 0;
        return (
          <div key={s.key} className={cn('dq-funnel__step', `dq-funnel__step--${s.tone}`)}>
            <div className="dq-funnel__count">{formatNumberCL(s.count)}</div>
            <div className="dq-funnel__label">{s.label}</div>
            <div className="dq-funnel__bar" aria-hidden>
              <div className="dq-funnel__bar-fill" style={{ width: `${pct.toFixed(1)}%` }} />
            </div>
            <div className="dq-funnel__pct">{pct.toFixed(0)}%</div>
          </div>
        );
      })}
    </section>
  );
}

// ─── Criterios por impacto ──────────────────────────────────────────────────
// Un solo bloque, lista ordenada por pass_rate ASC (worst primero). Bullet
// bars horizontales + count de fallas. Heatmap discreto por tono.

function CriteriaImpact({ data }) {
  const { t } = useTranslation('deliveryquality');
  if (!data || !data.by_criterion) return null;

  const rows = Object.entries(data.by_criterion)
    .filter(([key]) => CRITERION_META[key])
    .map(([key, c]) => {
      const passRate = (c.pass_rate ?? 0) * 100;
      const fails = data.total_evaluations - Math.round((c.pass_rate ?? 0) * data.total_evaluations);
      return { key, ...CRITERION_META[key], passRate, fails, extras: c };
    })
    .sort((a, b) => a.passRate - b.passRate);

  if (rows.length === 0) return null;

  return (
    <SectionHeader
      title={t('Por dónde se está perdiendo calidad')}
      hint={t('Ordenado por % de aprobación (peor primero).')}
    >
      <div className="dq-criteria">
        {rows.map((r) => {
          const Icon = r.icon;
          const tone = r.passRate < 80 ? 'critical' : r.passRate < 90 ? 'warn' : 'good';
          return (
            <div key={r.key} className={cn('dq-criteria__row', `dq-criteria__row--${tone}`)}>
              <div className="dq-criteria__main">
                <div className="dq-criteria__head">
                  <Icon className="dq-criteria__icon" />
                  <span className="dq-criteria__label">{t(r.label)}</span>
                  <span className="dq-criteria__hint">{t(r.hint)}</span>
                </div>
                <div className="dq-criteria__bar" aria-hidden>
                  <div className="dq-criteria__bar-fill" style={{ width: `${r.passRate.toFixed(1)}%` }} />
                </div>
              </div>
              <div className="dq-criteria__stats">
                <div className="dq-criteria__rate">{r.passRate.toFixed(0)}%</div>
                <div className="dq-criteria__fails">
                  {formatNumberCL(r.fails)} {t('fallaron')}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </SectionHeader>
  );
}

// ─── Drivers que necesitan atención ─────────────────────────────────────────

function DriversAttention({ state, onSelect, selectedId }) {
  const { t } = useTranslation('deliveryquality');
  const { data, loading, error } = state;

  if (loading) {
    return (
      <SectionHeader title={t('Repartidores que necesitan atención')}>
        <div className="dq-list">
          {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-14 rounded-lg" />)}
        </div>
      </SectionHeader>
    );
  }
  if (error || !data) return null;

  const rows = (data.drivers || data.items || data.data || data)
    .filter((d) => (d.evaluations || d.total_evaluations || 0) >= 3)
    .sort((a, b) => (a.avg_score ?? 100) - (b.avg_score ?? 100))
    .slice(0, 5);

  if (rows.length === 0) {
    return (
      <SectionHeader title={t('Repartidores que necesitan atención')}>
        <EmptyState
          icon={<ShieldCheck />}
          title={t('Nadie en alerta este período')}
          description={t('Todos los repartidores con al menos 3 evaluaciones están por encima del piso.')}
        />
      </SectionHeader>
    );
  }

  return (
    <SectionHeader
      title={t('Repartidores que necesitan atención')}
      hint={t('Los 5 con peor score y al menos 3 evaluaciones. Click para auditar.')}
    >
      <div className="dq-list">
        {rows.map((d) => (
          <DriverRow
            key={d.key || d.driver_id || d.id || d.user?.email}
            driver={d}
            variant="warn"
            onSelect={onSelect}
            selectedId={selectedId}
          />
        ))}
      </div>
    </SectionHeader>
  );
}

function TopPerformers({ state, onSelect, selectedId }) {
  const { t } = useTranslation('deliveryquality');
  const { data, loading, error } = state;

  if (loading) {
    return (
      <SectionHeader title={t('Mejores del período')}>
        <div className="dq-list">
          {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-14 rounded-lg" />)}
        </div>
      </SectionHeader>
    );
  }
  if (error || !data) return null;

  const rows = (data.drivers || data.items || data.data || data)
    .filter((d) => (d.evaluations || d.total_evaluations || 0) >= 3)
    .sort((a, b) => (b.avg_score ?? 0) - (a.avg_score ?? 0))
    .slice(0, 5);

  if (rows.length === 0) return null;

  return (
    <SectionHeader title={t('Mejores del período')}>
      <div className="dq-list">
        {rows.map((d) => (
          <DriverRow
            key={d.key || d.driver_id || d.id || d.user?.email}
            driver={d}
            variant="good"
            onSelect={onSelect}
            selectedId={selectedId}
          />
        ))}
      </div>
    </SectionHeader>
  );
}

function DriverRow({ driver, variant, onSelect, selectedId }) {
  const { t } = useTranslation('deliveryquality');
  // El endpoint by-driver entrega el nombre anidado en `user` (igual que la
  // vista vieja DriverTab: r.user.fullName). El email es buen fallback antes
  // de caer a "Sin nombre".
  const name =
    driver.user?.fullName ||
    driver.user?.name ||
    driver.name || driver.driver_name || driver.full_name ||
    driver.user?.email ||
    t('Sin nombre');
  const evals = driver.evaluations || driver.total_evaluations || 0;
  const score = driver.avg_score ?? 0;
  const initials = name.split(/\s+/).filter(Boolean).slice(0, 2).map((s) => s[0]).join('').toUpperCase();
  const scoreTone = score < 70 ? 'critical' : score < 85 ? 'warn' : 'good';
  // `key` es el id del repartidor (deliverer id) usado en breakdown/history/timeline.
  const id = driver.key || driver.driver_id || driver.id;
  const active = id != null && id === selectedId;
  return (
    <button
      type="button"
      className={cn('dq-driver', `dq-driver--${variant}`, active && 'dq-driver--active')}
      onClick={() => id != null && onSelect && onSelect(id)}
      aria-pressed={active}
      disabled={id == null}
    >
      <span className={cn('dq-driver__avatar', `dq-driver__avatar--${scoreTone}`)} aria-hidden>
        {initials || '··'}
      </span>
      <div className="dq-driver__body">
        <span className="dq-driver__name">{name}</span>
        <span className="dq-driver__meta">{formatNumberCL(evals)} {t('evaluadas')}</span>
      </div>
      <div className="dq-driver__scoreblock">
        <span className={cn('dq-driver__score', `dq-driver__score--${scoreTone}`)}>
          {score.toFixed(1)}
        </span>
        <span className="dq-driver__scorelabel">{t('score')}</span>
      </div>
    </button>
  );
}

// ─── Tendencia ──────────────────────────────────────────────────────────────

function TrendChart({ filters, isDark }) {
  const { t } = useTranslation('deliveryquality');
  // El endpoint /timeline bucketiza por `granularity` (día/semana/mes). Sin el
  // param el back agrega y devuelve un solo punto → la gráfica "solo muestra
  // hoy". Lo pedimos explícito como hace el TimelineTab viejo (contrato real).
  const { data, loading, error } = useEndpoint(
    () => deliveryQualityService.getTimeline({ ...filters, granularity: 'day' }),
    [filters],
  );

  if (loading) {
    return (
      <SectionHeader title={t('Tendencia del score')}>
        <Skeleton className="h-56 rounded-xl" />
      </SectionHeader>
    );
  }
  if (error || !data) return null;

  // Cada punto viene como { bucket: 'YYYY-MM-DD', avg_score, total_evaluations }
  // (mismo shape que consume TimelineTab). OJO: el campo de fecha es `bucket`;
  // leerlo PRIMERO es lo que arregla el bug — si cae a undefined, dayjs(undefined)
  // resuelve a HOY y los N puntos colapsan en una sola fecha. Orden cronológico
  // agnóstico vía el bucket ISO (el back no garantiza orden).
  const series = (data.points || data.timeline || data.data || data)
    .map((p) => {
      const bucket = p.bucket || p.date || p.bucket_date || p.day;
      return {
        bucket: String(bucket ?? ''),
        date: dayjs(bucket).format('DD MMM'),
        score: Number((p.avg_score ?? p.score ?? 0).toFixed(1)),
        evaluations: p.total_evaluations ?? p.evaluations ?? p.count ?? 0,
      };
    })
    .sort((a, b) => a.bucket.localeCompare(b.bucket));

  if (series.length === 0) return null;

  // Banda útil 70-100: el rango normal se acota a [70,100] para que se vea la
  // variacion real (con [0,100] la linea queda pegada arriba y plana). Si algun
  // punto cae bajo 70, el piso baja escalonado de 10 en 10 (decena inferior) para
  // NO recortar ese dato (lo grave debe verse), nunca por debajo de 0.
  const minScore = Math.min(...series.map((p) => p.score));
  const yFloor = minScore < 70 ? Math.max(0, Math.floor(minScore / 10) * 10) : 70;

  const stroke = 'var(--color-primary, #F97316)';
  const grid = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';
  const tick = 'rgb(var(--fg-muted))';
  return (
    <SectionHeader title={t('Tendencia del score')} hint={t('Promedio por día del rango.')}>
      <div className="dq-trend">
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart data={series} margin={{ top: 8, right: 12, left: -20, bottom: 0 }}>
            <defs>
              <linearGradient id="dqTrendFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={stroke} stopOpacity={0.18} />
                <stop offset="100%" stopColor={stroke} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke={grid} vertical={false} />
            <XAxis dataKey="date" tick={{ fill: tick, fontSize: 11 }} axisLine={false} tickLine={false} />
            <YAxis domain={[yFloor, 100]} tick={{ fill: tick, fontSize: 11 }} axisLine={false} tickLine={false} />
            <Tooltip
              contentStyle={{
                background: 'var(--surface-primary)',
                border: '1px solid var(--border-secondary)',
                borderRadius: 8,
                fontSize: 12,
              }}
              labelStyle={{ color: 'rgb(var(--fg))' }}
            />
            <Area
              type="monotone"
              dataKey="score"
              stroke={stroke}
              strokeWidth={2}
              fill="url(#dqTrendFill)"
              dot={false}
              activeDot={{ r: 3 }}
              isAnimationActive={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </SectionHeader>
  );
}

// ─── Layout primitives ──────────────────────────────────────────────────────

function SectionHeader({ title, hint, children }) {
  return (
    <section className="dq-section">
      <div className="dq-section__head">
        <h2 className="dq-section__title">{title}</h2>
        {hint && <p className="dq-section__hint">{hint}</p>}
      </div>
      {children}
    </section>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function useEndpoint(factory, deps) {
  const [state, setState] = useState({ data: null, loading: true, error: null });
  useEffect(() => {
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    Promise.resolve()
      .then(() => factory())
      .then((res) => {
        if (cancelled) return;
        const payload = res?.data ?? res ?? null;
        setState({ data: payload, loading: false, error: null });
      })
      .catch((err) => {
        if (cancelled) return;
        setState({ data: null, loading: false, error: err?.message || 'error' });
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return state;
}

function passRateFromCriteria(byCriterion) {
  if (!byCriterion) return 0;
  const rates = Object.values(byCriterion).map((c) => c?.pass_rate ?? 0);
  if (rates.length === 0) return 0;
  // "pasó todos los criterios" se aproxima como producto (asume independencia).
  // El back no devuelve la rate exacta de all-pass, así que reportamos el peor
  // criterio como proxy más conservador del "pass" del paquete.
  return Math.min(...rates);
}
